import React, { useState, useEffect, useRef } from 'react';
import iaPortrait from '../../../assets/conference/iaPortrait.png';
import humanPortrait from '../../../assets/conference/min-DSC06463fx.jpg';

const SpeakerOrb = ({ isTalking, analyserRef }) => {
    const [audioLevel, setAudioLevel] = useState(0);
    const animationIdRef = useRef(null);
    const dataArrayRef = useRef(null);

    useEffect(() => {
        if (!isTalking) {
            if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
            setAudioLevel(0);
            return;
        }

        const analyze = () => {
            if (!analyserRef?.current) {
                animationIdRef.current = requestAnimationFrame(analyze);
                return;
            }

            const analyser = analyserRef.current;
            if (!dataArrayRef.current || dataArrayRef.current.length !== analyser.frequencyBinCount) {
                dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
            }

            analyser.getByteFrequencyData(dataArrayRef.current);

            // Average of voice frequencies (first 60 bins)
            const usefulBins = Math.min(60, dataArrayRef.current.length);
            let sum = 0;
            for (let i = 0; i < usefulBins; i++) {
                sum += dataArrayRef.current[i];
            }
            const avg = sum / usefulBins / 255;
            setAudioLevel(avg);

            animationIdRef.current = requestAnimationFrame(analyze);
        };

        analyze();
        return () => {
            if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
        };
    }, [isTalking, analyserRef]);

    // orbSize en rem pour scaler avec le font-size root (grand écran / projecteur)
    // 4rem = 64px à 16px root → 96px à 1920px (×1.5), 128px à 2560px (×2)
    const orbSizeRem = 4;
    const ringCount = 3;

    return (
        <div className="relative flex items-center justify-center" style={{ width: `${orbSizeRem + 3}rem`, height: `${orbSizeRem + 3}rem` }}>
            {/* Concentric waves */}
            {Array.from({ length: ringCount }).map((_, i) => {
                const delay = i * 0.15;
                const baseScale = 1 + (i + 1) * 0.25;
                const audioScale = isTalking ? baseScale + audioLevel * 0.4 * (i + 1) : baseScale;
                const baseOpacity = isTalking ? 0.4 - i * 0.1 : 0.1;
                const audioOpacity = isTalking ? baseOpacity + audioLevel * 0.2 : baseOpacity;

                return (
                    <div
                        key={i}
                        className="absolute rounded-full border transition-all"
                        style={{
                            width: `${orbSizeRem}rem`,
                            height: `${orbSizeRem}rem`,
                            borderColor: isTalking
                                ? `rgba(34, 211, 238, ${audioOpacity})`
                                : `rgba(100, 116, 139, ${audioOpacity})`,
                            borderWidth: isTalking ? 2 : 1,
                            transform: `scale(${audioScale})`,
                            transitionDuration: '120ms',
                            transitionDelay: `${delay}s`,
                            boxShadow: isTalking && audioLevel > 0.3
                                ? `0 0 ${8 + audioLevel * 20}px rgba(34, 211, 238, ${audioLevel * 0.4})`
                                : 'none',
                        }}
                    />
                );
            })}

            {/* Glow behind avatar when talking */}
            {isTalking && (
                <div
                    className="absolute rounded-full transition-opacity duration-300"
                    style={{
                        width: `${orbSizeRem + 0.5}rem`,
                        height: `${orbSizeRem + 0.5}rem`,
                        background: `radial-gradient(circle, rgba(34, 211, 238, ${0.15 + audioLevel * 0.25}) 0%, transparent 70%)`,
                    }}
                />
            )}

            {/* Avatar circle */}
            <div
                className="relative rounded-full overflow-hidden border-2 transition-all duration-300"
                style={{
                    width: `${orbSizeRem}rem`,
                    height: `${orbSizeRem}rem`,
                    borderColor: isTalking ? 'rgb(34, 211, 238)' : 'rgb(100, 116, 139)',
                }}
            >
                <img
                    src={isTalking ? iaPortrait : humanPortrait}
                    alt={isTalking ? "IA Ministre" : "Président"}
                    className="w-full h-full object-cover transition-opacity duration-500"
                />
            </div>

            {/* Status dot */}
            <div
                className={`absolute bottom-0 right-2 w-3 h-3 rounded-full border-2 border-slate-900 transition-colors duration-300 ${isTalking ? 'bg-emerald-400 animate-pulse' : ''
                    }`}
            />
        </div>
    );
};

export default SpeakerOrb;
